import { useState } from 'react';

interface SignInResponse {
  [key: string]: string; // Assuming the structure is dynamic with encrypted keys and values as strings
}

interface DecryptedData {
  [key: string]: string | null;
}

//TODO keep required Env only post confirmation
const WEBSITE_ENVIRONMENT_URL_CONFIG = {
  LOCAL_HOST: 'localhost',
  DEV_URL: 'http://web.fireflink.com',
  TEST_URL: 'http://webtest.fireflink.com',
  PRE_PROD_URL: 'https://websitepreprod.fireflink.com',
  PRODUCTION1_URL: 'https://webprod.fireflink.com',
  PRODUCTION_URL: 'https://www.fireflink.com',
  ALPHA_URL: 'https://webalpha.fireflink.com',
  BETA_URL: 'http://webbeta.fireflink.com',
  DEV_WEB_SERVICE_URL: 'http://devwswb.fireflink.com',
  FIRE_CLOUD: 'http://cloud.fireflink.com',
  TEST2_URL: 'https://webtest3.fireflink.com',
  DEV2_URL: 'https://webtest3.fireflink.com',
  TEST3_URL: 'https://webtest3.fireflink.com',
  APP_V3_URL: 'https://app.v3.fireflink.com',
};
//TODO need to move to library util
const importKey = async (): Promise<CryptoKey> => {
  const encodedKey = new TextEncoder().encode(
    '12345678901234567890123456789012',
  );
  return await crypto.subtle.importKey(
    'raw',
    encodedKey,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
};

const decryptDataWithAES = async (
  SignInResponse: SignInResponse,
): Promise<DecryptedData | null> => {
  try {
    const encryptionKey = await importKey();
    const decryptedData: DecryptedData = {};

    for (const [encryptedKey, value] of Object.entries(SignInResponse)) {
      // Decrypt the key
      const [ivKeyBase64, encryptedKeyBase64] = encryptedKey.split(':');
      const ivKey = new Uint8Array(
        Array.from(atob(ivKeyBase64), c => c.charCodeAt(0)),
      );
      const encryptedKeyArray = new Uint8Array(
        Array.from(atob(encryptedKeyBase64), c => c.charCodeAt(0)),
      );
      const decryptedKeyBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: ivKey },
        encryptionKey,
        encryptedKeyArray,
      );
      const decryptedKey = new TextDecoder().decode(decryptedKeyBuffer);

      // Decrypt the value
      const [ivBase64, ciphertextBase64] = value.split(':');
      const iv = new Uint8Array(
        Array.from(atob(ivBase64), c => c.charCodeAt(0)),
      );
      const ciphertextArray = new Uint8Array(
        Array.from(atob(ciphertextBase64), c => c.charCodeAt(0)),
      );
      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        encryptionKey,
        ciphertextArray,
      );
      const decryptedText = new TextDecoder().decode(decryptedBuffer);
      decryptedData[decryptedKey] =
        decryptedText === 'null' ? null : decryptedText;
    }

    return decryptedData;
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

export const parseAndDecryptData = async (
  dataParam: string,
): Promise<DecryptedData | null> => {
  try {
    const sanitizedDataParam = dataParam.replace(/\+/g, '%2B');
    const decodedData = decodeURIComponent(
      sanitizedDataParam.replace(/ /g, '+'),
    );
    const parsedData = JSON.parse(decodedData);
    return await decryptDataWithAES(parsedData);
  } catch (error) {
    console.error('Failed to parse and decrypt data parameter:', error);
    return null;
  }
};

//TODO Need to keep only for required env
const environmentUrls: Record<string, string> = {
  //appv3: WEBSITE_ENVIRONMENT_URL_CONFIG.APP_V3_URL,
  alpha: WEBSITE_ENVIRONMENT_URL_CONFIG.ALPHA_URL,
  beta: WEBSITE_ENVIRONMENT_URL_CONFIG.BETA_URL,
  'dev.': WEBSITE_ENVIRONMENT_URL_CONFIG.DEV_URL,
  'test.': WEBSITE_ENVIRONMENT_URL_CONFIG.TEST_URL,
  //'app-v3': WEBSITE_ENVIRONMENT_URL_CONFIG.APP_V3_URL,
  onprem: window.location.hostname,
  product: window.location.hostname,
  preprod: WEBSITE_ENVIRONMENT_URL_CONFIG.PRE_PROD_URL,
  // app: WEBSITE_ENVIRONMENT_URL_CONFIG.PRODUCTION_URL,
  prod: WEBSITE_ENVIRONMENT_URL_CONFIG.PRODUCTION1_URL,
  devwswb: WEBSITE_ENVIRONMENT_URL_CONFIG.DEV_WEB_SERVICE_URL,
  cloud: WEBSITE_ENVIRONMENT_URL_CONFIG.FIRE_CLOUD,
  dev2: WEBSITE_ENVIRONMENT_URL_CONFIG.DEV2_URL,
  //test2: WEBSITE_ENVIRONMENT_URL_CONFIG.TEST2_URL,
  test3: WEBSITE_ENVIRONMENT_URL_CONFIG.TEST3_URL,
};

// export const getWebSiteHostName = (): string => {
//   const urlData = window.location.host;
//   if (urlData.includes('localhost')) {
//     return window.location.host;
//   }
//   for (const key in environmentUrls) {
//     if (urlData.includes(key)) {
//       console.log(environmentUrls[key], 'environmentUrls[key]');
//       return environmentUrls[key];
//     }
//   }
//   console.log(window.location.hostname, 'hostname');
//   return window.location.hostname;
// };
export const getWebSiteHostName = (): string => {
  const urlData = window.location.host;

  if (urlData.includes('localhost')) {
    return window.location.host;
  }

  const sortedKeys = Object.keys(environmentUrls).sort(
    (a, b) => b.length - a.length,
  );

  for (const key of sortedKeys) {
    if (urlData.includes(key)) {
      return environmentUrls[key];
    }
  }

  return window.location.origin;
};

export const useHideChatbot = () => {
  const [isIconVisible, setIsIconVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsIconVisible(true);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return {
    isIconVisible,
    isModalOpen,
    openModal,
    closeModal,
  };
};
